import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  buildTree,
  pathLabel,
  type Section,
  type SectionNode,
  sectionDestinations,
  useCreateSection,
  useDeleteSection,
  useMoveSection,
  usePlaceResource,
  useRenameSection,
} from '@/entities/section';
import {
  groupDestinations,
  groupPath,
  useMoveGroup,
  type WorkspaceGroup,
} from '@/entities/workspace-group';
import { isApiError } from '@/shared/api';
import { FormError } from '@/shared/forms';
import { Button, Dialog, Icons, TextField } from '@/shared/ui';

import styles from './Sections.module.css';

export const SECTION_NAME_MAX = 100;

/** Create a section (top level, or below `parent`) or rename `section`. */
export function SectionNameDialog({
  workspaceId,
  parent,
  section,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  parent: Section | null;
  /** Present: rename this section instead of creating one. */
  section?: Section | undefined;
  onClose: () => void;
  onCreated?: ((section: Section) => void) | undefined;
}) {
  const { t } = useTranslation();
  const create = useCreateSection(workspaceId);
  const rename = useRenameSection(workspaceId);
  const [name, setName] = useState(section?.name ?? t('sections.defaultName'));
  const mutation = section ? rename : create;
  const title = section
    ? t('sections.renameTitle', { name: section.name })
    : parent
      ? t('sections.createSubTitle', { parent: parent.name })
      : t('sections.createTitle');
  const submit = () => {
    const value = name.trim();
    if (value === '' || mutation.isPending) return;
    if (section) {
      rename.mutate(
        { id: section.id, name: value, expected_version: section.version },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        { name: value, parent_id: parent?.id ?? null },
        {
          onSuccess: (created) => {
            onCreated?.(created);
            onClose();
          },
        },
      );
    }
  };
  return (
    <Dialog title={title} onClose={onClose}>
      <form
        className={styles.form}
        data-testid="section-name-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t('sections.nameLabel')}
          value={name}
          maxLength={SECTION_NAME_MAX}
          autoFocus
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <FormError error={mutation.error} />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending || name.trim() === ''}
          >
            {section ? t('common.save') : t('sections.create')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Deleting a section never deletes what is in it. An empty one simply goes; one that still holds
 * something is refused by the server until the person explicitly chooses to move the contents up
 * one level (then only the container is deleted).
 */
export function DeleteSectionDialog({
  workspaceId,
  section,
  looksEmpty,
  onClose,
}: {
  workspaceId: string;
  section: Section;
  /** What the viewer can see inside; the server has the last word (it may hold hidden items). */
  looksEmpty: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteSection(workspaceId);
  const [needMove, setNeedMove] = useState(!looksEmpty);
  const refused = isApiError(remove.error) && remove.error.code === 'SECTION_NOT_EMPTY';
  return (
    <Dialog title={t('sections.deleteTitle', { name: section.name })} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (remove.isPending) return;
          remove.mutate(
            { id: section.id, contents: needMove ? 'move_to_parent' : 'reject' },
            {
              onSuccess: onClose,
              onError: (error) => {
                if (isApiError(error) && error.code === 'SECTION_NOT_EMPTY') setNeedMove(true);
              },
            },
          );
        }}
      >
        <p className={styles.note}>
          {needMove || refused ? t('sections.deleteFullText') : t('sections.deleteEmptyText')}
        </p>
        {!refused && <FormError error={remove.error} />}
        <div className={styles.actions}>
          <Button type="submit" variant="danger" disabled={remove.isPending}>
            {needMove || refused ? t('sections.deleteMove') : t('sections.deleteConfirm')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function flatten(nodes: readonly SectionNode[]): SectionNode[] {
  return nodes.flatMap((node) => [node, ...flatten(node.children)]);
}

export type MoveSubject =
  | {
      kind: 'board' | 'database' | 'document';
      id: string;
      name: string;
      sectionId: string | null;
      groupId?: string | null;
    }
  | { kind: 'section'; id: string; name: string; sectionId: string | null }
  | { kind: 'group'; id: string; name: string; sectionId: string | null; groupId: string | null };

interface Place {
  section: string | null;
  group: string | null;
}

/**
 * "Move to section": a destination picker for a board, a database or a section - the workspace
 * root and every section as an indented list with its full path as the accessible name. The
 * current place is marked, invalid destinations (a section's own subtree, too deep) are disabled.
 * Works without any dragging.
 */
export function MoveToSectionDialog({
  workspaceId,
  subject,
  sections,
  maxDepth,
  groups = [],
  maxGroupDepth = 8,
  onClose,
}: {
  workspaceId: string;
  subject: MoveSubject;
  sections: readonly Section[];
  maxDepth: number;
  /** Groups are destinations too (not for a section, which never lives in a group). */
  groups?: readonly WorkspaceGroup[];
  maxGroupDepth?: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const place = usePlaceResource(workspaceId);
  const moveSection = useMoveSection(workspaceId);
  const moveGroup = useMoveGroup(workspaceId);
  const mutation =
    subject.kind === 'section' ? moveSection : subject.kind === 'group' ? moveGroup : place;
  const current: Place = {
    section: subject.sectionId,
    group: subject.kind === 'section' ? null : (subject.groupId ?? null),
  };
  const [target, setTarget] = useState<Place>(current);
  const options = flatten(buildTree(sections));
  const allowed =
    subject.kind === 'section' ? sectionDestinations(sections, subject.id, maxDepth) : null;
  const groupsAllowed =
    subject.kind === 'group' ? groupDestinations(groups, subject.id, maxGroupDepth) : null;
  const unchanged = target.section === current.section && target.group === current.group;
  /** The groups of one section (or the root), each indented under its parent. */
  const groupChoices = (sectionId: string | null, sectionDepth: number) => {
    if (subject.kind === 'section') return [];
    const prefix = sectionId ? `${pathLabel(sections, sectionId)} / ` : '';
    return groups
      .filter((g) => g.section_id === sectionId)
      .map((g) => ({ group: g, path: groupPath(groups, g.id) }))
      .sort((a, b) => {
        const left = a.path.map((g) => g.name).join('/');
        const right = b.path.map((g) => g.name).join('/');
        return left.localeCompare(right);
      })
      .map(({ group, path }) => {
        const disabled = groupsAllowed !== null && !groupsAllowed.has(group.id);
        const here = current.group === group.id;
        return (
          <li key={group.id}>
            <label
              className={styles.destination}
              data-disabled={disabled || undefined}
              style={{ '--depth': Math.min(sectionDepth + path.length, 5) } as React.CSSProperties}
              title={prefix + path.map((g) => g.name).join(' / ')}
            >
              <input
                type="radio"
                name="destination"
                checked={target.group === group.id}
                disabled={disabled}
                aria-label={prefix + path.map((g) => g.name).join(' / ')}
                onChange={() => {
                  setTarget({ section: group.section_id, group: group.id });
                }}
              />
              <Icons.group />
              <span className={styles.destinationName}>{group.name}</span>
              {here && <span className={styles.currentMark}>{t('sections.current')}</span>}
            </label>
          </li>
        );
      });
  };
  const choice = (id: string | null, label: string, title: string, depth: number) => {
    const disabled = allowed !== null && !allowed.has(id);
    return (
      <li key={id ?? 'root'}>
        <label
          className={styles.destination}
          data-disabled={disabled || undefined}
          style={{ '--depth': Math.min(depth, 4) } as React.CSSProperties}
          title={title}
        >
          <input
            type="radio"
            name="destination"
            checked={target.section === id && target.group === null}
            disabled={disabled}
            aria-label={title}
            onChange={() => {
              setTarget({ section: id, group: null });
            }}
          />
          {id === null ? <Icons.workspace /> : <Icons.folder />}
          <span className={styles.destinationName}>{label}</span>
          {id === current.section && current.group === null && (
            <span className={styles.currentMark}>{t('sections.current')}</span>
          )}
          {disabled && id !== null && (
            <span className={styles.currentMark}>{t('sections.tooDeep')}</span>
          )}
        </label>
      </li>
    );
  };
  return (
    <Dialog title={t('sections.moveDialogTitle', { name: subject.name })} onClose={onClose}>
      <form
        className={styles.form}
        data-testid="move-to-section"
        onSubmit={(event) => {
          event.preventDefault();
          if (mutation.isPending || unchanged) return;
          if (subject.kind === 'section') {
            moveSection.mutate(
              { id: subject.id, parent_id: target.section },
              { onSuccess: onClose },
            );
          } else if (subject.kind === 'group') {
            moveGroup.mutate(
              { id: subject.id, section_id: target.section, parent_group_id: target.group },
              { onSuccess: onClose },
            );
          } else {
            place.mutate(
              {
                kind: subject.kind,
                id: subject.id,
                section_id: target.section,
                ...(target.group !== null && { group_id: target.group }),
              },
              { onSuccess: onClose },
            );
          }
        }}
      >
        <p className={styles.note}>{t('sections.moveHint')}</p>
        <fieldset className={styles.destinations}>
          <legend className="visually-hidden">{t('sections.moveGroup')}</legend>
          <ul className={styles.destinationList}>
            {choice(null, t('sections.root'), t('sections.root'), 0)}
            {groupChoices(null, 0)}
            {options.flatMap((node) => [
              choice(node.id, node.name, pathLabel(sections, node.id), node.depth),
              ...groupChoices(node.id, node.depth),
            ])}
          </ul>
        </fieldset>
        <FormError error={mutation.error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={mutation.isPending || unchanged}>
            {t('sections.moveConfirm')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
